/**
 * V9-7-4 + V9-7-5: Real-time workspace event stream with multi-tab dedup.
 *
 * Connects to GET /api/w/:slug/events (SSE) and invalidates TanStack
 * Query cache keys on `invalidate` events. Uses BroadcastChannel for
 * multi-tab leader election so only one tab maintains the EventSource
 * connection; other tabs receive events via the broadcast channel.
 *
 * Features:
 *   - Exponential backoff reconnect (1s → 2s → 4s → 8s → 16s → 30s cap)
 *   - Tab visibility: disconnect when hidden, reconnect when visible
 *   - Leader election via BroadcastChannel (V9-7-5)
 *   - Graceful fallback when BroadcastChannel is unavailable
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useWorkspace } from '../lib/workspace';
import { invalidationMap } from '../lib/query-keys';

const CHANNEL_NAME = 'fedsec-workspace-events';
const LEADER_HEARTBEAT_MS = 10_000;
const LEADER_TIMEOUT_MS = 15_000;
const MAX_BACKOFF_MS = 30_000;

interface InvalidateMessage {
  type: 'invalidate';
  keys: string[];
  timestamp: string;
}

interface LeaderMessage {
  type: 'leader-heartbeat' | 'leader-claim' | 'leader-resign';
  tabId: string;
  slug: string;
}

type ChannelMessage = InvalidateMessage | LeaderMessage;

/**
 * Hook that connects to the workspace event stream and invalidates
 * query cache on mutation events from other tabs/users.
 *
 * Wire into any component rendered inside WorkspaceGate (e.g. AppShell).
 */
export function useWorkspaceEvents(): void {
  const queryClient = useQueryClient();
  const { workspace } = useWorkspace();
  const slug = workspace?.slug ?? '';

  // Stable refs to avoid re-running the effect on every render.
  const slugRef = useRef(slug);
  slugRef.current = slug;

  useEffect(() => {
    if (!slug) return;

    const tabId = crypto.randomUUID();
    let isLeader = false;
    let eventSource: EventSource | null = null;
    let channel: BroadcastChannel | null = null;
    let leaderHeartbeatTimer: ReturnType<typeof setInterval> | null = null;
    let leaderTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
    let backoffMs = 1000;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let disposed = false;

    // ---- Invalidation handler (shared by leader and follower) ----

    function handleInvalidation(keys: string[]) {
      const currentSlug = slugRef.current;
      if (!currentSlug) return;

      for (const key of keys) {
        // Check if this key maps to a known invalidation group.
        const mapKey = `${key}:changed` as keyof typeof invalidationMap;
        if (mapKey in invalidationMap) {
          const queryKeys = invalidationMap[mapKey](currentSlug);
          for (const qk of queryKeys) {
            queryClient.invalidateQueries({ queryKey: qk });
          }
        } else {
          // Fallback: invalidate by prefix match on the raw key + slug.
          queryClient.invalidateQueries({ queryKey: [key, currentSlug] });
        }
      }
    }

    // ---- BroadcastChannel setup ----

    try {
      channel = new BroadcastChannel(`${CHANNEL_NAME}-${slug}`);
    } catch {
      // BroadcastChannel unavailable (e.g. some privacy modes).
      // This tab will always be the leader.
      channel = null;
    }

    function broadcastInvalidation(msg: InvalidateMessage): void {
      try {
        channel?.postMessage(msg);
      } catch { /* channel closed */ }
    }

    function broadcastLeader(type: LeaderMessage['type']): void {
      try {
        channel?.postMessage({ type, tabId, slug } satisfies LeaderMessage);
      } catch { /* channel closed */ }
    }

    // ---- EventSource management (leader only) ----

    function connectSSE(): void {
      if (disposed || !isLeader) return;

      const url = `/api/w/${encodeURIComponent(slugRef.current)}/events`;
      eventSource = new EventSource(url, { withCredentials: true });

      eventSource.addEventListener('invalidate', (e) => {
        try {
          const data = JSON.parse(e.data) as InvalidateMessage;
          handleInvalidation(data.keys);
          // Relay to other tabs.
          broadcastInvalidation({
            type: 'invalidate',
            keys: data.keys,
            timestamp: data.timestamp,
          });
        } catch { /* malformed event */ }
      });

      eventSource.addEventListener('connected', () => {
        backoffMs = 1000; // Reset backoff on successful connect.
      });

      eventSource.addEventListener('ping', () => {
        // Keep-alive; no action needed.
      });

      eventSource.onerror = () => {
        closeSSE();
        scheduleReconnect();
      };
    }

    function closeSSE(): void {
      if (eventSource) {
        eventSource.close();
        eventSource = null;
      }
    }

    function scheduleReconnect(): void {
      if (disposed || !isLeader) return;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        connectSSE();
      }, backoffMs);
      backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS);
    }

    // ---- Leader election ----

    function becomeLeader(): void {
      if (isLeader) return;
      isLeader = true;
      broadcastLeader('leader-claim');
      connectSSE();

      // Heartbeat so followers know the leader is alive.
      leaderHeartbeatTimer = setInterval(() => {
        broadcastLeader('leader-heartbeat');
      }, LEADER_HEARTBEAT_MS);
    }

    function resignLeadership(): void {
      if (!isLeader) return;
      isLeader = false;
      broadcastLeader('leader-resign');
      closeSSE();
      if (leaderHeartbeatTimer) {
        clearInterval(leaderHeartbeatTimer);
        leaderHeartbeatTimer = null;
      }
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    }

    function resetLeaderTimeout(): void {
      if (leaderTimeoutTimer) clearTimeout(leaderTimeoutTimer);
      leaderTimeoutTimer = setTimeout(() => {
        // Leader timed out — claim leadership.
        becomeLeader();
      }, LEADER_TIMEOUT_MS);
    }

    if (channel) {
      channel.onmessage = (e: MessageEvent<ChannelMessage>) => {
        const msg = e.data;
        if (msg.type === 'invalidate') {
          // Received from leader tab — apply locally.
          handleInvalidation((msg as InvalidateMessage).keys);
        } else if (msg.type === 'leader-claim' || msg.type === 'leader-heartbeat') {
          const leader = msg as LeaderMessage;
          if (leader.tabId !== tabId && leader.slug === slug) {
            // Another tab is leader — stand down.
            if (isLeader) resignLeadership();
            resetLeaderTimeout();
          }
        } else if (msg.type === 'leader-resign') {
          // Leader resigned — race to claim.
          setTimeout(() => {
            if (!isLeader && !disposed) becomeLeader();
          }, Math.random() * 500);
        }
      };

      // Announce presence; wait briefly then claim if no leader responds.
      resetLeaderTimeout();
      // Short initial delay with jitter to avoid thundering herd.
      setTimeout(() => {
        if (!disposed && !isLeader) {
          becomeLeader();
        }
      }, Math.random() * 300 + 100);
    } else {
      // No BroadcastChannel — always be leader.
      becomeLeader();
    }

    // ---- Tab visibility ----

    function handleVisibility(): void {
      if (document.hidden) {
        if (isLeader) {
          resignLeadership();
        }
      } else {
        // Tab became visible — try to become leader if there isn't one.
        if (!isLeader) {
          if (channel) {
            resetLeaderTimeout();
          } else {
            becomeLeader();
          }
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibility);

    // ---- Cleanup ----

    return () => {
      disposed = true;
      resignLeadership();
      document.removeEventListener('visibilitychange', handleVisibility);
      if (leaderTimeoutTimer) clearTimeout(leaderTimeoutTimer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { channel?.close(); } catch { /* already closed */ }
    };
  }, [slug, queryClient]);
}
